import type { Instance as PopperInstance } from '@popperjs/core';
import { createPopper } from '@popperjs/core';
import clsx from 'clsx';
import type { HTMLAttributes, ReactNode, ForwardRefRenderFunction } from 'react';
import { useState, forwardRef, useRef, useEffect, useContext } from 'react';
import { Collapse } from 'react-bootstrap';
import { BsChevronDown, BsChevronRight } from 'react-icons/bs';

import BayBadge from '../../../BayBadge';
import { SidebarContext } from '../MenuWrapper';

interface SubMenuProps extends Omit<HTMLAttributes<HTMLLIElement>, 'prefix'> {
	children?: ReactNode;
	icon?: ReactNode;
	className?: string;
	title?: string;
	defaultOpen?: boolean;
	showChevron?: boolean;
	open?: boolean;
	prefix?: ReactNode;
	suffix?: ReactNode;
	firstchild?: boolean;
	popperarrow?: boolean;
	onOpenChange?: (closed: boolean) => void;
	stage?: {
		color: string;
		bgValue: string;
		textValue: string;
		text: string;
	};
}

const SubMenu: ForwardRefRenderFunction<HTMLLIElement, SubMenuProps> = (
	{
		children,
		icon,
		className,
		title,
		defaultOpen = false,
		showChevron = true,
		open,
		prefix,
		suffix,
		firstchild,
		popperarrow,
		onOpenChange,
		stage,
		...rest
	},
	ref,
) => {
	let popperInstance: PopperInstance | null = null;
	const { collapsed, rtl, toggled } = useContext(SidebarContext);
	const [closed, setClosed] = useState(!defaultOpen);
	const popperElRef = useRef<HTMLDivElement>(null);
	const referenceElement = useRef<HTMLDivElement>(null);
	const popperElement = useRef<HTMLDivElement>(null);

	// Sync the `closed` state with `defaultOpen`. Required for dynamically generated Template Library sub-pages in the side navigation.
	useEffect(() => {
		setClosed(!defaultOpen);
	}, [defaultOpen]);

	const handleToggleSubMenu = () => {
		if (onOpenChange) onOpenChange(closed);
		setClosed(!closed);
	};

	useEffect(() => {
		if (firstchild) {
			if (collapsed) {
				if (referenceElement.current && popperElement.current) {
					popperInstance = createPopper(
						referenceElement.current,
						popperElement.current,
						{
							placement: 'right',
							strategy: 'fixed',
							modifiers: [
								{
									name: 'computeStyles',
									options: {
										adaptive: false,
									},
								},
							],
						},
					);
				}

				if (popperElRef.current) {
					const ro = new ResizeObserver(() => {
						if (popperInstance) {
							popperInstance.update();
						}
					});

					ro.observe(popperElRef.current);
					if (referenceElement.current) {
						ro.observe(referenceElement.current);
					}
				}

				setTimeout(() => {
					if (popperInstance) {
						popperInstance.update();
					}
				}, 300);
			}
		}

		return () => {
			if (popperInstance) {
				popperInstance.destroy();
				popperInstance = null;
			}
		};
	}, [collapsed, rtl, toggled]);

	return (
		<li
			ref={ref}
			className={clsx(
				'bay-sidenav-menu-item bay-sidenav-sub-menu',
				className,
				{
					open: typeof open === 'undefined' ? !closed : open,
				},
			)}
			{...rest}
		>
			<div
				ref={referenceElement}
				className="bay-sidenav-inner-item"
				role="button"
				tabIndex={0}
				onClick={handleToggleSubMenu}
				onKeyPress={handleToggleSubMenu}
			>
				{icon ? (
					<span className="bay-sidenav-icon-wrapper mx-2">
						<span className="bay-sidenav-icon">{icon}</span>
					</span>
				) : null}
				{prefix ? <span className="prefix-wrapper">{prefix}</span> : null}
				<span className="bay-sidenav-item-content d-flex ps-1">
					<div className="nav-link--title text-truncate">{title}</div>
					{stage && (
						<div className="mx-2">
							<BayBadge
								bg={stage.color}
								bgValue={stage.bgValue}
								className="w-100"
								isLight
								size="md"
								text={stage.color}
								textValue={stage.textValue}
							>
								{stage.text}
							</BayBadge>
						</div>
					)}
				</span>
				{suffix ? <span className="suffix-wrapper">{suffix}</span> : null}
				{showChevron && (
					<span className="bay-sidenav-arrow-wrapper">
						{closed ? (
							<BsChevronRight size={14} />
						) : (
							<BsChevronDown size={14} />
						)}
					</span>
				)}
			</div>

			{firstchild && collapsed ? (
				<div
					ref={popperElement}
					className={clsx('bay-sidenav-inner-list-item popper-element', {
						'has-arrow': popperarrow,
					})}
				>
					<div
						ref={popperElRef}
						className={`popper-inner scrollbar-sm ${collapsed ? 'sideBarCollapsed-popper-inner' : ''}`}
					>
						<ul>{children}</ul>
					</div>
					{popperarrow ? (
						<div data-popper-arrow className="popper-arrow" />
					) : null}
				</div>
			) : (
				<Collapse
					className="bay-sidenav-inner-list-item"
					in={typeof open === 'undefined' ? !closed : open}
				>
					<div>
						<ul>{children}</ul>
					</div>
				</Collapse>
			)}
		</li>
	);
};

export default forwardRef(SubMenu);
